import { GetDeliveryMomentsWithSkuResponse, LocationSilo } from '@next/core-lib/cart';
import { createEntityAdapter, EntityAdapter, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import { LocationSiloWithProductList, SiloProductItem } from 'src/app/components/silos/models/silo-product-list.models';

import * as ProductActions from './actions';
import { GetVariant } from '../models/product-delivery.models';
import { ExpandedSilosState, ProductState, SkuCatalogItemViewData } from '../models/product.models';

export const silosAdapter: EntityAdapter<LocationSilo> = createEntityAdapter<LocationSilo>({
  selectId: (silo) => silo.id,
});

export const silosWithProductListAdapter: EntityAdapter<LocationSiloWithProductList> = createEntityAdapter<LocationSiloWithProductList>({
  selectId: (silo) => silo.id,
});

export const variantsAdapter: EntityAdapter<GetVariant> = createEntityAdapter<GetVariant>({
  selectId: (variant) => variant.description,
});

export const proposalDeliveryMomentsAdapter = createEntityAdapter<GetDeliveryMomentsWithSkuResponse>({
  selectId: (deliveryMoment) => deliveryMoment.skuId,
});

export const proposalDeliveryMomentsZeroFlexibilityAdapter = createEntityAdapter<GetDeliveryMomentsWithSkuResponse>({
  selectId: (deliveryMoment) => deliveryMoment.skuId,
});

export const orderHistorySkuInformationAdapter = createEntityAdapter<SkuCatalogItemViewData>({
  selectId: (skuCatalogItem) => skuCatalogItem.skuId,
});

export const initialState: ProductState = {
  skuCatalogItems: null,
  skuCatalogItemsNotAvailable: null,
  skuCatalogItemsLoaded: null,
  silosWithProductList: silosWithProductListAdapter.getInitialState([]),
  silosWithProductListNotAvailable: null,
  silosWithProductListLoaded: null,
  silos: silosAdapter.getInitialState([]),
  silosNotAvailable: null,
  silosLoaded: null,
  silosInUseOnDates: null,
  deliveryMoments: null,
  deliveryMomentsNotAvailable: null,
  deliveryMomentsLoaded: null,
  variants: variantsAdapter.getInitialState([]),
  variantsNotAvailable: null,
  variantsLoaded: null,
  selectedCartLine: null,
  selectedProductSiloLine: null,
  proposalDeliveryMoments: proposalDeliveryMomentsAdapter.getInitialState([]),
  proposalZeroFlexibilityDeliveryMoments: proposalDeliveryMomentsZeroFlexibilityAdapter.getInitialState([]),
  orderHistorySkuInformation: orderHistorySkuInformationAdapter.getInitialState([]),
  favoritesLoading: null,
  favoriteSkus: [],
};

export const expandedSilosState: ExpandedSilosState = {
  expandedSiloIds: [],
};

export const expandedSilosReducer = createReducer<ExpandedSilosState>(
  expandedSilosState,
  on(ProductActions.setExpandedSiloIds, (state, { siloIds }) => ({
    ...state,
    expandedSiloIds: siloIds,
  })),
);

export const productReducer = createReducer<ProductState>(
  initialState,
  on(ProductActions.skuCatalogRequest, (state) => ({
    ...state,
    skuCatalogItems: null,
    skuCatalogItemsLoaded: false,
  })),
  on(ProductActions.getFavoritesSuccess, (state, { skus }) => ({
    ...state,
    favoriteSkus: skus,
    favoritesLoading: false,
  })),
  on(ProductActions.skuCatalogSuccess, (state, { skuCatalogItems }) => ({
    ...state,
    skuCatalogItems,
    skuCatalogItemsNotAvailable: !skuCatalogItems?.length,
    skuCatalogItemsLoaded: true,
  })),
  on(ProductActions.getFavorites, (state) => ({
    ...state,
    favoritesLoading: true,
  })),
  on(ProductActions.favoritesCatalogFailure, (state) => ({
    ...state,
    favoritesCatalogItems: null,
    favoritesLoading: false,
  })),
  on(ProductActions.skuCatalogFailure, (state) => ({
    ...state,
    skuCatalogItems: null,
    skuCatalogItemsNotAvailable: true,
    skuCatalogItemsLoaded: false,
  })),
  on(ProductActions.resetSkuCatalog, (state) => ({
    ...state,
    skuCatalogItems: null,
  })),
  on(ProductActions.resetFavoritesCatalog, (state) => ({
    ...state,
    favoritesCatalogItems: null,
  })),
  on(ProductActions.getSilosWithProductListRequest, (state, _) => ({
    ...state,
    silosWithProductList: silosAdapter.removeAll(state.silosWithProductList),
    silosWithProductListLoaded: false,
  })),
  on(ProductActions.getSilosWithProductListSuccess, (state, { silos }) => ({
    ...state,
    silosWithProductList: silosWithProductListAdapter.setAll(silos, state.silosWithProductList),
    silosWithProductListNotAvailable: !silos?.length,
    silosWithProductListLoaded: true,
  })),
  on(ProductActions.getSilosRequest, (state, _) => ({
    ...state,
    silos: silosAdapter.removeAll(state.silos),
    silosLoaded: false,
  })),
  on(ProductActions.getSilosSuccess, (state, { silos }) => ({
    ...state,
    silos: silosAdapter.setAll(silos, state.silos),
    silosNotAvailable: !silos?.length,
    silosLoaded: true,
  })),
  on(ProductActions.addSilo, (state, { silo }) => ({
    ...state,
    silos: silosAdapter.addOne(silo, state.silos),
    silosWithProductList: silosWithProductListAdapter.addOne({ ...silo, productList: [] }, state.silosWithProductList),
  })),
  on(ProductActions.updateSilo, (state, { id, silo }) => {
    const currentSiloWithProductList = state.silosWithProductList.entities[id];
    const mergedChanges = currentSiloWithProductList ? { ...currentSiloWithProductList, ...silo } : silo;
    return {
      ...state,
      silos: silosAdapter.updateOne({ id, changes: silo }, state.silos),
      silosWithProductList: silosWithProductListAdapter.updateOne({ id, changes: mergedChanges }, state.silosWithProductList),
    };
  }),
  on(ProductActions.deleteSilo, (state, { id }) => ({
    ...state,
    silos: silosAdapter.removeOne(id, state.silos),
    silosWithProductList: silosWithProductListAdapter.removeOne(id, state.silosWithProductList),
  })),
  on(ProductActions.deleteSiloProductListItem, (state, { siloId, productId }) => ({
    ...state,
    silosWithProductList: silosWithProductListAdapter.updateOne(
      {
        id: siloId,
        changes: {
          productList: state.silosWithProductList.entities[siloId]?.productList.filter((item) => item.id !== productId),
        },
      },
      state.silosWithProductList,
    ),
  })),
  on(ProductActions.getSilosFailed, (state) => ({
    ...state,
    silos: silosAdapter.removeAll(state.silos),
    silosNotAvailable: true,
    silosLoaded: false,
  })),
  on(ProductActions.getSilosWithProductListFailed, (state) => ({
    ...state,
    silos: silosWithProductListAdapter.removeAll(state.silosWithProductList),
    silosWithProductListNotAvailable: true,
    silosWithProductListLoaded: false,
  })),
  on(ProductActions.resetSilos, (state) => ({
    ...state,
    silos: silosAdapter.removeAll(state.silos),
    silosLoaded: null,
  })),
  on(ProductActions.getSilosInUseOnDatesRequest, (state, _) => ({
    ...state,
    silosInUseOnDates: undefined,
  })),
  on(ProductActions.getSilosInUseOnDatesSuccess, (state, { silosInUseOnDates }) => ({
    ...state,
    silosInUseOnDates,
  })),
  on(ProductActions.getSilosInUseOnDatesFailed, (state) => ({
    ...state,
    silosInUseOnDates: undefined,
  })),
  on(ProductActions.resetSilosInUseOnDates, (state) => ({
    ...state,
    silosInUseOnDates: null,
  })),
  on(ProductActions.getSkuDeliveryMomentsRequest, (state, _) => ({
    ...state,
    deliveryMoments: undefined,
    deliveryMomentsLoaded: false,
  })),
  on(ProductActions.getDeliveryMomentsSuccess, (state, { deliveryMoments }) => ({
    ...state,
    deliveryMoments,
    deliveryMomentsNotAvailable: !deliveryMoments,
    deliveryMomentsLoaded: true,
  })),
  on(ProductActions.getDeliveryMomentsFailed, (state) => ({
    ...state,
    deliveryMoments: undefined,
    deliveryMomentsNotAvailable: true,
    deliveryMomentsLoaded: false,
  })),
  on(ProductActions.resetDeliveryMoments, (state) => ({
    ...state,
    deliveryMoments: null,
    deliveryMomentsLoaded: null,
  })),
  on(ProductActions.getVariantsRequest, (state, _) => ({
    ...state,
    variants: variantsAdapter.removeAll(state.variants),
    variantsLoaded: false,
  })),
  on(ProductActions.getVariantsSuccess, (state, { variants }) => ({
    ...state,
    variants: variantsAdapter.setAll(variants, state.variants),
    variantsNotAvailable: !variants?.length,
    variantsLoaded: true,
  })),
  on(ProductActions.getVariantsFailed, (state) => ({
    ...state,
    variants: variantsAdapter.removeAll(state.variants),
    variantsNotAvailable: true,
    variantsLoaded: false,
  })),
  on(ProductActions.resetVariants, (state) => ({
    ...state,
    variants: variantsAdapter.removeAll(state.variants),
    variantsLoaded: null,
  })),
  on(ProductActions.getCartLineDeliveryMomentsRequest, (state) => ({
    ...state,
    deliveryMoments: undefined,
    deliveryMomentsLoaded: false,
  })),
  on(ProductActions.getCartLineDeliveryMomentsSuccess, (state, { deliveryMoments }) => ({
    ...state,
    deliveryMoments,
    deliveryMomentsNotAvailable: !deliveryMoments,
    deliveryMomentsLoaded: true,
  })),
  on(ProductActions.getCartLineDeliveryMomentsFailed, (state) => ({
    ...state,
    deliveryMoments: undefined,
    deliveryMomentsNotAvailable: true,
    deliveryMomentsLoaded: false,
  })),
  on(ProductActions.resetCartLineDeliveryMoments, (state) => ({
    ...state,
    deliveryMoments: null,
    deliveryMomentsLoaded: null,
  })),
  on(ProductActions.setSelectedCartLine, (state, { selectedCartLine }) => ({
    ...state,
    selectedCartLine,
  })),
  on(ProductActions.resetSelectedCartLine, (state) => ({
    ...state,
    selectedCartLine: null,
  })),
  on(ProductActions.setSelectedProductSiloLine, (state, { selectedProductSiloLine }) => ({
    ...state,
    selectedProductSiloLine,
  })),
  on(ProductActions.resetSelectedProductSiloLine, (state) => ({
    ...state,
    selectedProductSiloLine: null,
  })),
  on(ProductActions.setSilosProductSupplement, (state, { siloId, skuId, supplements }) => {
    const locationSilo = state.silosWithProductList?.entities[siloId];

    if (!locationSilo) {
      return state;
    }

    const updatedProductList = locationSilo.productList.map((product) =>
      product.id === skuId
        ? {
            ...product,
            supplements,
          }
        : product,
    );

    return {
      ...state,
      silosWithProductList: {
        ...state.silosWithProductList,
        entities: {
          ...state.silosWithProductList?.entities,
          [siloId]: {
            ...locationSilo,
            productList: updatedProductList,
          },
        },
      },
    };
  }),
  on(ProductActions.addProductToSiloProductListSuccess, (state, { response }) => {
    const siloUpdates: Update<LocationSiloWithProductList>[] = [];
    response.forEach((siloProductItemResponse) => {
      const addedProduct = siloProductItemResponse;
      const updatedSilo = state.silosWithProductList.entities[siloProductItemResponse.siloId];
      const updatedProductlist: SiloProductItem[] = [addedProduct, ...updatedSilo.productList.filter((p) => p.id !== addedProduct.id)];

      const mergedChanges: LocationSiloWithProductList = {
        ...updatedSilo,
        productList: [...updatedProductlist],
      };

      siloUpdates.push({ id: addedProduct.siloId, changes: mergedChanges });
    });

    const newState: ProductState = {
      ...state,
      silosWithProductList: silosWithProductListAdapter.updateMany(siloUpdates, state.silosWithProductList),
    };

    return newState;
  }),
  on(ProductActions.getDeliveryProposalsRequest, (state) => ({
    ...state,
    proposalDeliveryMoments: proposalDeliveryMomentsAdapter.removeAll(state.proposalDeliveryMoments),
    proposalZeroFlexibilityDeliveryMoments: proposalDeliveryMomentsZeroFlexibilityAdapter.removeAll(state.proposalZeroFlexibilityDeliveryMoments),
  })),
  on(ProductActions.getDeliveryProposalsRequestSuccess, (state, { proposalDeliveryMoments }) => ({
    ...state,
    proposalDeliveryMoments: proposalDeliveryMomentsAdapter.setAll(proposalDeliveryMoments.deliveryDates, state.proposalDeliveryMoments),
    proposalZeroFlexibilityDeliveryMoments: proposalDeliveryMomentsZeroFlexibilityAdapter.setAll(
      proposalDeliveryMoments.zeroFlexibilityDeliveryDates,
      state.proposalZeroFlexibilityDeliveryMoments,
    ),
  })),
  on(ProductActions.getProductInformationsRequestSuccess, (state, { productInformation }) => ({
    ...state,
    orderHistorySkuInformation: orderHistorySkuInformationAdapter.setAll(productInformation, state.orderHistorySkuInformation),
  })),
);
